var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { useState } from 'react';
import { useEvent, useTranslate } from 'react-admin';
import { Fade, Typography, styled } from '@mui/material';
import { useAutoSave } from './useAutoSave';
/**
 * A component that enables autosaving of the form and displays the last save date.
 *
 * @param interval The interval in milliseconds between two autosaves. Defaults to 5000 (5s).
 * @param confirmationDuration The delay in milliseconds before save confirmation message disappears. Defaults to 3000 (3s).
 * @param typographyProps Additional props to pass to the `<Typography>` component that displays the last save time.
 *
 * @example
 * import { AutoSave } from '@react-admin/ra-form-layout';
 * import { Edit, SaveButton, SimpleForm, TextInput, Toolbar } from 'react-admin';
 *
 * const AutoSaveToolbar = () => (
 *    <Toolbar>
 *       <SaveButton />
 *       <AutoSave />
 *   </Toolbar>
 * );
 *
 * const PostEdit = () => (
 *     <Edit mutationMode="optimistic">
 *         <SimpleForm toolbar={AutoSaveToolbar} resetOptions={{ keepDirtyValues: true }}>
 *             <TextInput source="title" />
 *             <TextInput source="teaser" />
 *         </SimpleForm>
 *     </Edit>
 * );
 */
export var AutoSave = function (_a) {
    var _b = _a.confirmationDuration, confirmationDuration = _b === void 0 ? 3000 : _b, typographyProps = _a.typographyProps, onSuccess = _a.onSuccess, onError = _a.onError, props = __rest(_a, ["confirmationDuration", "typographyProps", "onSuccess", "onError"]);
    var _c = useState(null), lastSaveAt = _c[0], setLastSaveAt = _c[1];
    var _d = useState(null), error = _d[0], setError = _d[1];
    var translate = useTranslate();
    var timeoutRef = React.useRef(undefined);
    var handleSuccess = useEvent(onSuccess !== null && onSuccess !== void 0 ? onSuccess : noop);
    var handleError = useEvent(onError !== null && onError !== void 0 ? onError : noop);
    var isSaving = useAutoSave(__assign(__assign({}, props), { onSuccess: function (response, variables, context) {
            setLastSaveAt(new Date());
            setError(null);
            // To avoid TS taking the Node setTimeout instead of the browser one
            var setTimeout = window.setTimeout, clearTimeout = window.clearTimeout;
            if (confirmationDuration !== false) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(function () {
                    setLastSaveAt(null);
                }, confirmationDuration);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (handleSuccess) {
                handleSuccess(response, variables, context);
            }
        }, onError: function (error) {
            setError((error === null || error === void 0 ? void 0 : error.message) || error);
            if (handleError) {
                handleError(error);
            }
        } }));
    if (error) {
        return (_jsx(Root, __assign({ color: "error", className: AutoSaveClasses.error }, typographyProps, { children: translate('ra-form-layout.autosave.error', {
                _: 'Server error, changes are not saved: %{error}',
                error: error,
            }) })));
    }
    if (isSaving) {
        return (_jsx(Root, __assign({ color: "text.secondary" }, typographyProps, { children: translate('ra-form-layout.autosave.saving', {
                _: 'Saving...',
            }) })));
    }
    return (_jsx(Fade, { in: !!lastSaveAt, children: _jsx(Root, __assign({ color: "text.secondary" }, typographyProps, { children: translate('ra-form-layout.autosave.last_saved_at', {
                _: 'All changes saved',
                lastSaveAt: lastSaveAt,
            }) })) }));
};
var PREFIX = 'RaAutoSave';
export var AutoSaveClasses = {
    error: "".concat(PREFIX, "-error"),
};
var Root = styled(Typography, {
    name: PREFIX,
    overridesResolver: function (_props, styles) { return styles.root; },
})(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1)
        },
        _b["&.".concat(AutoSaveClasses.error)] = {},
        _b);
});
var noop = function () {
    /* no-op */
};
